import {
  OccurrenceChatPageResponse,
  OccurrenceChatUnreadResponse,
  occurrenceChatId,
} from "@edu/shared";
import { call, callVoid } from "../../lib/api.ts";

/**
 * Пять ручек чата занятия: страница, отправка, удаление, счётчик непрочитанного
 * и отметка прочтения.
 *
 * Живут здесь, а не в `lib/api.ts`, по той же соседской причине, что окно
 * переписки (`features/announcements/conversations-api.ts`): схемы нужны ровно
 * этим экранам, а базовый адрес, cookie и разбор отказов приезжают общие, через
 * `call`.
 *
 * ⚠️ **Своя папка, а не папка комнаты, и переезд сюда случился 10.09.2026 вместе
 * с карточкой занятия.** До неё модуль лежал в `features/room/`, и в его шапке
 * стояло предупреждение: «когда за ручками придёт карточка, модулю, скорее
 * всего, придётся переехать в место, общее для комнаты и расписания». Читателей
 * теперь трое (комната, всплывающая карточка расписания, строка списка уроков),
 * и оставить модуль в комнате значило бы, что расписание тянет к себе её чанк.
 *
 * ⚠️ **Адрес собирает `occurrenceChatId` из контрактов, а не конкатенация по
 * месту.** Строка составная (`lesson:<урок>`, `pair:<слот>:<дата>`,
 * `group:<группа>:<дата>`), ездит в адресе четырёх ручек, и то, что несколько
 * мест собирают руками, разъедется на первой же правке разделителя.
 *
 * ⚠️ **Адресов у чата ДВА, а ключ один** (раздел 3 `docs/room-chat-design.md`).
 * Из комнаты клиент адресует УРОКОМ, из расписания парой «слот и дата» либо
 * группой; сводит их к ключу сервер, в одной функции. Выводить ключ занятия из
 * урока на клиенте нельзя: `slot_id` у урока это слабая ссылка, при
 * пересоздании слота она обнуляется, и выводимый адрес менял бы форму на
 * середине жизни урока.
 */

/**
 * Адрес чата уроком. Одно место сборки на весь клиент.
 *
 * Так адресуют двое: комната урока (у неё другого адреса нет вовсе) и строка
 * списка уроков. Занятие серии сервер разрешает из урока в ключ пары либо
 * группы сам, в одной функции.
 */
export function lessonChatId(lessonId: string): string {
  return occurrenceChatId({ kind: "lesson", lessonId });
}

function path(chatId: string): string {
  /* Двоеточие в сегменте пути законно, но полагаться на это в трёх местах хуже,
     чем закодировать в одном (тот же довод, что у ключа разговора) */
  return `/v1/occurrence-chats/${encodeURIComponent(chatId)}`;
}

/**
 * Страница чата: свежие ПЕРВЫМИ, `cursor` уводит к более старым.
 *
 * ⚠️ Её же зовёт опрос свежести открытой ленты, поэтому лимит частоты на сервере
 * у неё щедрый (240 в минуту по сессии) и разобран в шапке ручки.
 */
export function getOccurrenceChatPage(
  chatId: string,
  cursor?: string,
): Promise<OccurrenceChatPageResponse> {
  const query = cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`;
  return call(`${path(chatId)}/messages${query}`, {}, OccurrenceChatPageResponse);
}

/**
 * Отправить сообщение.
 *
 * ⚠️ **Ответ намеренно не разбирается схемой и не используется.** Ручка отдаёт
 * идентификатор и время созданной строки, а ленте нужно другое: подпись автора
 * ГЛАЗАМИ ЧИТАТЕЛЯ и состояние окна письма, то есть то, что собирает страница.
 * Подставить строку самому значило бы завести второе место сборки сообщения,
 * которое разойдётся с первым на первой же правке правила подписей.
 */
export function sendOccurrenceChatMessage(chatId: string, body: string): Promise<void> {
  return callVoid(`${path(chatId)}/messages`, { method: "POST", body: { body } });
}

/**
 * Убрать сообщение: преподаватель любое своего занятия, ученик только своё.
 *
 * ⚠️ **Разницу решает предикат сервера, а не ветка на экране.** Экран прячет
 * кнопку там, где ручка ответит отказом (кнопка, ведущая в отказ, хуже
 * отсутствующей), но правило живёт в `WHERE`, и второго его экземпляра здесь
 * нет.
 */
export function deleteOccurrenceChatMessage(chatId: string, messageId: string): Promise<void> {
  return callVoid(`${path(chatId)}/messages/${encodeURIComponent(messageId)}`, {
    method: "DELETE",
  });
}

/**
 * Отметить чат прочитанным.
 *
 * ⚠️ **Отдельная ручка от счётчика, и слить их нельзя** (раздел 7 проекта):
 * первый же опрос гасил бы непрочитанное навсегда. Отказ проглатывается у
 * вызывающего молча: это наша запись о работе человека, а не его работа.
 */
export function markOccurrenceChatRead(chatId: string): Promise<void> {
  return callVoid(`${path(chatId)}/read`, { method: "POST" });
}

/**
 * Дешёвый счётчик непрочитанного по ВСЕМ занятиям окна: три поля на занятие, ни
 * одного тела и ни одного имени.
 *
 * ⚠️ **Окно называется явно, а не угадывается сервером** (раздел 7 проекта):
 * человек листает недели, и точки обязаны приезжать про ту, на которую он
 * смотрит. Сутки назад дописывает сервер сам: писать можно ещё сутки после
 * конца занятия.
 *
 * ⚠️ **`pinned` это ПОДСКАЗКА, а не адрес.** Ответ режется полусотней строк, и
 * открытое занятие поднимается ею в начало сортировки; незнакомый и мусорный
 * ключ ничего не поднимает и ничем не отказывает. Без неё счётчик слепнет на
 * пятьдесят первом занятии молча, и это не гипотеза: ровно так ослеп кабинетный
 * счётчик, и это чинили правкой.
 */
export function getOccurrenceChatUnread(args: {
  from: string;
  to: string;
  pinned?: string | null;
}): Promise<OccurrenceChatUnreadResponse> {
  const query = new URLSearchParams({ from: args.from, to: args.to });
  if (args.pinned !== undefined && args.pinned !== null) query.set("id", args.pinned);
  return call(`/v1/occurrence-chats/unread?${query.toString()}`, {}, OccurrenceChatUnreadResponse);
}
